// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useState } from 'react';
import { Linking, Modal, Platform, Pressable, StyleSheet, View } from 'react-native';

import GoffyButton from '@/components/GoffyButton';
import GoffyInput from '@/components/GoffyInput';
import { Text } from '@/components/Themed';
import {
  API_TOKEN_NAME_MAX_LENGTH,
  developerDocsUrl,
  exportFileName,
  type ApiToken,
  type CreatedApiToken,
} from '@/features/profile/apiTokens';
import { useApiTokens } from '@/hooks/useApiTokens';
import { useI18n } from '@/i18n';
import { shareImageFromUrl } from '@/services/shareImage';
import appColors, { alpha } from '@/theme/colors';

type Props = {
  apiBaseUrl: string;
  token?: string | null;
  disabled?: boolean;
  /** The profile screen keeps its coach mark off while any dialog on the screen is open. */
  onDialogOpenChange?: (open: boolean) => void;
};

type CreateStep = 'closed' | 'name' | 'reveal';

/**
 * Profile section for the user data API: the user's personal access tokens (create, one-time
 * reveal, revoke) and the "download my data" export. The secret exists in this component's state
 * only while the reveal modal is open and is cleared the moment it closes.
 */
export default function ApiAccessSection({ apiBaseUrl, token, disabled = false, onDialogOpenChange }: Props) {
  const { t, locale } = useI18n();
  const { tokens, loading, busy, error, create, revoke, clearError } = useApiTokens({ apiBaseUrl, token });
  const [createStep, setCreateStep] = useState<CreateStep>('closed');
  const [name, setName] = useState('');
  const [created, setCreated] = useState<CreatedApiToken | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [revokeTarget, setRevokeTarget] = useState<ApiToken | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const dialogOpen = createStep !== 'closed' || revokeTarget !== null;
  useEffect(() => {
    onDialogOpenChange?.(dialogOpen);
  }, [dialogOpen, onDialogOpenChange]);

  const formatDate = useCallback(
    (iso: string) => {
      try {
        return new Date(iso).toLocaleDateString(locale);
      } catch {
        return iso.slice(0, 10);
      }
    },
    [locale]
  );

  const openCreate = () => {
    clearError();
    setName('');
    setCreated(null);
    setCopyState('idle');
    setCreateStep('name');
  };

  const closeCreate = () => {
    setCreateStep('closed');
    setName('');
    setCreated(null);
    setCopyState('idle');
    clearError();
  };

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && trimmedName.length <= API_TOKEN_NAME_MAX_LENGTH && busy === null;

  const submitCreate = async () => {
    if (!canSubmit) return;
    const result = await create(trimmedName);
    if (result) {
      setCreated(result);
      setCreateStep('reveal');
    }
  };

  const copySecret = async () => {
    if (!created) return;
    setCopyState((await copyToClipboard(created.token)) ? 'copied' : 'failed');
  };

  const confirmRevoke = async () => {
    if (!revokeTarget || busy) return;
    const ok = await revoke(revokeTarget.id);
    if (ok) setRevokeTarget(null);
  };

  const handleExport = async () => {
    if (!token || exporting) return;
    setExporting(true);
    setExportMessage(null);
    try {
      const outcome = await shareImageFromUrl({
        url: `${apiBaseUrl}/api/me/export`,
        token,
        fileName: exportFileName(),
        mimeType: 'application/json',
        title: t('profile.exportTitle'),
      });
      if (outcome === 'downloaded') setExportMessage(t('profile.exportDownloaded'));
      else if (outcome === 'unsupported') setExportMessage(t('profile.exportUnsupported'));
    } catch (err) {
      console.warn('[profile] export failed', err);
      setExportMessage(t('profile.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const tokenMeta = (entry: ApiToken) => {
    const parts = [
      t('profile.apiTokenCreatedAt', { date: formatDate(entry.createdAt) }),
      entry.lastUsedAt ? t('profile.apiTokenLastUsed', { date: formatDate(entry.lastUsedAt) }) : t('profile.apiTokenNeverUsed'),
    ];
    if (entry.expiresAt) parts.push(t('profile.apiTokenExpires', { date: formatDate(entry.expiresAt) }));
    return parts.join(' · ');
  };

  return (
    <View style={styles.section}>
      <Text style={styles.label}>{t('profile.apiAccessSectionLabel')}</Text>
      <Text style={styles.meta}>{t('profile.apiAccessHint')}</Text>

      {loading ? (
        <Text style={styles.meta}>{t('profile.apiTokensLoading')}</Text>
      ) : tokens.length === 0 ? (
        <Text style={styles.meta}>{t('profile.apiTokensEmpty')}</Text>
      ) : (
        <View style={styles.list}>
          {tokens.map((entry) => (
            <View key={entry.id} style={styles.item} testID={`api-token-row-${entry.id}`}>
              <View style={styles.itemText}>
                <Text style={styles.itemName}>{entry.name}</Text>
                <Text style={styles.itemMeta}>{`${entry.tokenPrefix}…`}</Text>
                <Text style={styles.itemMeta}>{tokenMeta(entry)}</Text>
              </View>
              <View style={styles.itemActions}>
                <Pressable
                  style={[styles.removeButton, (disabled || busy !== null) && styles.buttonDisabled]}
                  onPress={() => {
                    clearError();
                    setRevokeTarget(entry);
                  }}
                  disabled={disabled || busy !== null}
                  testID={`api-token-revoke-${entry.id}`}
                  accessibilityLabel={t('profile.apiTokenRevoke')}
                >
                  <Text style={styles.removeText}>{t('profile.apiTokenRevoke')}</Text>
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      )}
      {error && !dialogOpen ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.buttonRow}>
        <GoffyButton
          label={t('profile.apiTokenCreate')}
          onPress={openCreate}
          disabled={disabled || busy !== null || !token}
          tone="dark"
          oldButtonStyle={styles.secondaryButton}
          oldTextStyle={styles.secondaryButtonText}
          oldDisabledStyle={styles.buttonDisabled}
          style={styles.secondaryButtonImageMode}
          textStyle={styles.secondaryButtonImageText}
          testID="api-token-create-open"
        />
        <GoffyButton
          label={exporting ? t('profile.exportPreparing') : t('profile.exportAction')}
          onPress={() => {
            void handleExport();
          }}
          disabled={disabled || exporting || !token}
          tone="dark"
          oldButtonStyle={styles.secondaryButton}
          oldTextStyle={styles.secondaryButtonText}
          oldDisabledStyle={styles.buttonDisabled}
          style={styles.secondaryButtonImageMode}
          textStyle={styles.secondaryButtonImageText}
          testID="export-my-data"
        />
      </View>
      <Text style={styles.meta}>{t('profile.exportHint')}</Text>
      {exportMessage ? <Text style={styles.meta}>{exportMessage}</Text> : null}

      <View style={styles.docsRow}>
        <Text style={styles.meta}>{t('profile.apiDocsHint')}</Text>
        <Pressable
          onPress={() => {
            void Linking.openURL(developerDocsUrl(locale));
          }}
          accessibilityRole="link"
          testID="api-docs-link"
        >
          <Text style={styles.link}>{t('profile.apiDocsLink')}</Text>
        </Pressable>
      </View>

      <Modal visible={createStep !== 'closed'} transparent animationType="fade" onRequestClose={closeCreate}>
        {/* No backdrop dismissal while the secret is on screen: a stray tap must not lose it. */}
        <Pressable style={styles.backdrop} onPress={createStep === 'name' ? closeCreate : undefined}>
          <Pressable style={styles.card} onPress={() => null} testID="api-token-create-modal">
            {createStep === 'reveal' && created ? (
              <>
                <Text style={styles.title}>{t('profile.apiTokenRevealTitle')}</Text>
                <Text style={styles.body}>{t('profile.apiTokenRevealWarning')}</Text>
                <View style={styles.secretBox}>
                  <Text style={styles.secret} selectable testID="api-token-secret">
                    {created.token}
                  </Text>
                </View>
                {copyState === 'copied' ? <Text style={styles.meta}>{t('profile.apiTokenCopied')}</Text> : null}
                {copyState === 'failed' ? <Text style={styles.error}>{t('profile.apiTokenCopyFailed')}</Text> : null}
                <View style={styles.actions}>
                  <Pressable
                    style={styles.cancelButton}
                    onPress={() => {
                      void copySecret();
                    }}
                    testID="api-token-copy"
                    accessibilityLabel={t('profile.apiTokenCopy')}
                  >
                    <Text style={styles.cancelButtonText}>{t('profile.apiTokenCopy')}</Text>
                  </Pressable>
                  <Pressable
                    style={styles.primaryButton}
                    onPress={closeCreate}
                    testID="api-token-done"
                    accessibilityLabel={t('profile.apiTokenDone')}
                  >
                    <Text style={styles.primaryButtonText}>{t('profile.apiTokenDone')}</Text>
                  </Pressable>
                </View>
              </>
            ) : (
              <>
                <Text style={styles.title}>{t('profile.apiTokenCreateTitle')}</Text>
                <Text style={styles.fieldLabel}>{t('profile.apiTokenNameLabel')}</Text>
                <GoffyInput
                  value={name}
                  onChangeText={setName}
                  placeholder={t('profile.apiTokenNamePlaceholder')}
                  maxLength={API_TOKEN_NAME_MAX_LENGTH}
                  autoFocus
                  autoCapitalize="sentences"
                  onSubmitEditing={() => {
                    void submitCreate();
                  }}
                  accessibilityLabel={t('profile.apiTokenNameLabel')}
                  testID="api-token-name"
                />
                {error ? <Text style={styles.error}>{error}</Text> : null}
                <View style={styles.actions}>
                  <Pressable
                    style={styles.cancelButton}
                    onPress={closeCreate}
                    testID="api-token-create-cancel"
                    accessibilityLabel={t('profile.apiTokenCancel')}
                  >
                    <Text style={styles.cancelButtonText}>{t('profile.apiTokenCancel')}</Text>
                  </Pressable>
                  <Pressable
                    style={[styles.primaryButton, !canSubmit && styles.buttonDisabled]}
                    onPress={() => {
                      void submitCreate();
                    }}
                    disabled={!canSubmit}
                    testID="api-token-create-submit"
                    accessibilityLabel={busy === 'create' ? t('profile.apiTokenCreating') : t('profile.apiTokenCreate')}
                  >
                    <Text style={styles.primaryButtonText}>
                      {busy === 'create' ? t('profile.apiTokenCreating') : t('profile.apiTokenCreate')}
                    </Text>
                  </Pressable>
                </View>
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={revokeTarget !== null}
        transparent
        animationType="fade"
        onRequestClose={() => {
          if (busy !== 'revoke') setRevokeTarget(null);
        }}
      >
        <Pressable
          style={styles.backdrop}
          onPress={() => {
            if (busy !== 'revoke') setRevokeTarget(null);
          }}
        >
          <Pressable style={[styles.card, styles.dangerCard]} onPress={() => null} testID="api-token-revoke-modal">
            <Text style={styles.title}>{t('profile.apiTokenRevokeConfirmTitle')}</Text>
            <Text style={styles.body}>{t('profile.apiTokenRevokeConfirmBody', { name: revokeTarget?.name ?? '' })}</Text>
            {error ? <Text style={styles.error}>{error}</Text> : null}
            <View style={styles.actions}>
              <Pressable
                style={styles.cancelButton}
                onPress={() => setRevokeTarget(null)}
                testID="api-token-revoke-cancel"
                accessibilityLabel={t('profile.apiTokenCancel')}
              >
                <Text style={styles.cancelButtonText}>{t('profile.apiTokenCancel')}</Text>
              </Pressable>
              <Pressable
                style={[styles.dangerButton, busy === 'revoke' && styles.buttonDisabled]}
                onPress={() => {
                  void confirmRevoke();
                }}
                disabled={busy === 'revoke'}
                testID="api-token-revoke-confirm"
                accessibilityLabel={t('profile.apiTokenRevokeConfirm')}
              >
                <Text style={styles.dangerButtonText}>
                  {busy === 'revoke' ? t('profile.apiTokenRevoking') : t('profile.apiTokenRevokeConfirm')}
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

/**
 * Web uses the async clipboard API; native uses the clipboard React Native still ships. Both are
 * best-effort — the secret stays selectable on screen, so a failed copy is an inconvenience, not
 * a lost token.
 */
const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (Platform.OS === 'web') {
      const clipboard = (globalThis as { navigator?: { clipboard?: { writeText?: (text: string) => Promise<void> } } })
        .navigator?.clipboard;
      if (!clipboard?.writeText) return false;
      await clipboard.writeText(value);
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const native = require('react-native') as { Clipboard?: { setString?: (text: string) => void } };
    if (!native.Clipboard?.setString) return false;
    native.Clipboard.setString(value);
    return true;
  } catch {
    return false;
  }
};

const styles = StyleSheet.create({
  section: {
    gap: 10,
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  label: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  meta: {
    color: appColors.accent.primary,
    fontSize: 12,
  },
  error: {
    color: appColors.status.dangerText,
    fontSize: 12,
  },
  list: {
    gap: 8,
  },
  item: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.accentMuted,
    backgroundColor: appColors.surface.muted,
    padding: 10,
    gap: 8,
  },
  itemText: {
    gap: 2,
  },
  itemName: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  itemMeta: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  itemActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  removeButton: {
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.status.dangerBg,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: appColors.status.dangerPanel,
  },
  removeText: {
    color: appColors.status.dangerTextSoft,
    fontWeight: '700',
    fontSize: 12,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  secondaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  secondaryButtonText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
  secondaryButtonImageMode: {
    width: 190,
    height: 56,
  },
  secondaryButtonImageText: {
    fontSize: 16,
  },
  docsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
  },
  link: {
    color: appColors.accent.primary,
    fontSize: 12,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.5),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: appColors.background.panel,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 16,
    gap: 12,
  },
  dangerCard: {
    borderColor: appColors.status.dangerBg,
  },
  title: {
    color: appColors.text.primary,
    fontSize: 18,
    fontWeight: '700',
  },
  body: {
    color: appColors.text.secondary,
    fontSize: 14,
    lineHeight: 20,
  },
  fieldLabel: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '600',
  },
  secretBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    backgroundColor: appColors.surface.input,
    padding: 12,
  },
  secret: {
    color: appColors.text.primary,
    fontSize: 13,
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', default: 'monospace' }),
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 4,
  },
  cancelButton: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  cancelButtonText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  primaryButton: {
    borderRadius: 12,
    backgroundColor: appColors.background.interactive,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  primaryButtonText: {
    color: appColors.text.inverse,
    fontSize: 14,
    fontWeight: '800',
  },
  dangerButton: {
    borderRadius: 12,
    backgroundColor: appColors.status.dangerBg,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  dangerButtonText: {
    color: appColors.status.dangerTextSoft,
    fontSize: 14,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
});
